/**
 * Extract positioned text items from a PDF using pdfjs-dist (browser).
 */

import * as pdfjsLib from 'pdfjs-dist';
import { logError, logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'extractPdfItems';

if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
}

/**
 * @typedef {{ text: string, x: number, y: number, w: number }} PdfTextItem
 */

/**
 * @param {import('pdfjs-dist').TextItem} item
 * @returns {PdfTextItem|null}
 */
function mapTextItem(item) {
  if (!item?.str || !String(item.str).trim()) return null;
  const transform = item.transform || [];
  return {
    text: String(item.str),
    x: transform[4] ?? 0,
    y: transform[5] ?? 0,
    w: item.width ?? 0,
  };
}

/**
 * @param {File|ArrayBuffer} fileOrBuffer
 * @returns {Promise<{ pages: Record<number, PdfTextItem[]>, pageCount: number }>}
 */
export async function extractPdfItemsFromFile(fileOrBuffer) {
  const arrayBuffer =
    fileOrBuffer instanceof ArrayBuffer
      ? fileOrBuffer
      : await fileOrBuffer.arrayBuffer();

  logInfo(LOG_CONTEXT, 'extractPdfItemsFromFile start', {
    byteLength: arrayBuffer.byteLength,
  });

  try {
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    /** @type {Record<number, PdfTextItem[]>} */
    const pages = {};

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const items = (textContent.items || [])
        .map(mapTextItem)
        .filter(Boolean);

      pages[pageNum] = items;
    }

    logInfo(LOG_CONTEXT, 'extractPdfItemsFromFile complete', {
      pageCount: pdf.numPages,
      itemCounts: Object.fromEntries(
        Object.entries(pages).map(([p, list]) => [p, list.length])
      ),
    });

    return { pages, pageCount: pdf.numPages };
  } catch (error) {
    logError(LOG_CONTEXT, 'extractPdfItemsFromFile failed', {
      message: error?.message,
    });
    throw new Error(`Failed to parse PDF: ${error?.message || 'unknown error'}`);
  }
}
